/**
 * The builder's Email panel (AgencyAnalytics' Email and Schedule): send a
 * link to this dashboard or report now, keep schedules, and see what went
 * out. Emails awaiting approval can be approved (they go out at once) or
 * rejected. Every email links to a frozen copy; nothing is attached.
 */

import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';

import type { RangeSetting } from '../reports-api.ts';
import { emailApi, type ReportSend, type Schedule, type ScheduleInput } from './email-api.ts';
import { EmailFields, splitRecipients } from './EmailFields.tsx';
import { describe, ScheduleForm } from './ScheduleForm.tsx';

const when = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });
const STATUS: Record<ReportSend['status'], string> = {
  AWAITING_APPROVAL: 'Awaiting approval',
  SENDING: 'Sending…',
  SENT: 'Sent',
  REJECTED: 'Rejected',
  FAILED: 'Failed',
};

function SendNow(props: {
  defaultSubject: string;
  onSend: (input: {
    recipients: string[];
    subject: string;
    message: string;
    allow: boolean;
  }) => Promise<void>;
}): ReactNode {
  const [value, setValue] = useState({ subject: props.defaultSubject, message: '' });
  const [recipientsText, setRecipientsText] = useState('');
  const [allow, setAllow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await props.onSend({ ...value, recipients: splitRecipients(recipientsText), allow });
      setRecipientsText('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="rth" onSubmit={(event) => void submit(event)}>
      <EmailFields
        value={value}
        recipientsText={recipientsText}
        onRecipientsText={setRecipientsText}
        onChange={(patch) => setValue((current) => ({ ...current, ...patch }))}
      />
      <label className="rs-check">
        <input
          type="checkbox"
          checked={allow}
          onChange={(event) => setAllow(event.target.checked)}
        />
        Allow recipients to change the date range
      </label>
      <button type="submit" className="btn small" disabled={busy || !recipientsText.trim()}>
        Send now
      </button>
    </form>
  );
}

export function EmailPanel({
  organisationId,
  documentId,
  documentName,
  range,
  onClose,
}: {
  organisationId: string;
  documentId: string;
  documentName: string;
  range: RangeSetting;
  onClose: () => void;
}): ReactNode {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [sends, setSends] = useState<ReportSend[]>([]);
  const [editing, setEditing] = useState<Schedule | 'new' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [nextSchedules, nextSends] = await Promise.all([
      emailApi.schedules(organisationId, documentId),
      emailApi.sends(organisationId, documentId),
    ]);
    setSchedules(nextSchedules);
    setSends(nextSends);
  }, [organisationId, documentId]);
  useEffect(() => {
    reload().catch(() => setProblem('Emails could not be loaded.'));
  }, [reload]);

  /** Runs a change, reloads, and shows a success note or the API's reason. */
  const act = (work: () => Promise<unknown>, success: string) =>
    work()
      .then(() => {
        setProblem(null);
        setNotice(success);
        return reload();
      })
      .catch((error: unknown) => {
        setNotice(null);
        setProblem(error instanceof Error ? error.message : 'That did not work. Try again.');
        throw error;
      });
  const saveSchedule = (input: ScheduleInput) =>
    act(
      () =>
        editing && editing !== 'new'
          ? emailApi.updateSchedule(organisationId, editing.id, input)
          : emailApi.createSchedule(organisationId, documentId, input),
      'Schedule saved.',
    ).then(() => setEditing(null));
  const toggle = (schedule: Schedule) =>
    void act(
      () =>
        emailApi.updateSchedule(organisationId, schedule.id, {
          ...schedule,
          active: !schedule.active,
        }),
      schedule.active ? 'Schedule paused.' : 'Schedule resumed.',
    ).catch(() => undefined);

  return (
    <aside className="rs" aria-label="Email">
      <header className="rs-head">
        <strong>Email</strong>
        <button type="button" className="rb-icon" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="rs-body">
        <p className="rs-explain">
          Recipients get a link to a read-only copy with live numbers — no sign-in, nothing
          attached.
        </p>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        {notice && (
          <p className="rh-access-msg" role="status">
            {notice}
          </p>
        )}
        <h3 className="rs-subhead">Send now</h3>
        <SendNow
          defaultSubject={documentName}
          onSend={({ recipients, subject, message, allow }) =>
            act(
              () =>
                emailApi.sendNow(organisationId, documentId, {
                  recipients,
                  subject,
                  message,
                  range,
                  allow_range_change: allow,
                }),
              'Sending now.',
            ).then(() => undefined)
          }
        />
        <h3 className="rs-subhead">Schedules</h3>
        {editing ? (
          <ScheduleForm
            initial={editing === 'new' ? null : editing}
            defaultSubject={documentName}
            onSave={saveSchedule}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <button type="button" className="btn small secondary" onClick={() => setEditing('new')}>
            New schedule
          </button>
        )}
        <ul className="rcm-threads">
          {schedules.map((schedule) => (
            <li key={schedule.id} className="rcm-thread">
              <strong>{describe(schedule)}</strong>
              <small>
                {schedule.active && schedule.next_run_at
                  ? `Next: ${when.format(new Date(schedule.next_run_at))}`
                  : 'Paused'}
                {` · ${schedule.recipients.length} recipient${schedule.recipients.length === 1 ? '' : 's'}`}
                {schedule.require_approval ? ' · needs approval' : ''}
              </small>
              <div className="sp-actions">
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => setEditing(schedule)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => toggle(schedule)}
                >
                  {schedule.active ? 'Pause' : 'Resume'}
                </button>
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => {
                    if (!window.confirm('Delete this schedule? Emails already sent are kept.'))
                      return;
                    void act(
                      () => emailApi.deleteSchedule(organisationId, schedule.id),
                      'Schedule deleted.',
                    ).catch(() => undefined);
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
        <h3 className="rs-subhead">Activity</h3>
        {sends.length === 0 && <p className="rs-explain">Nothing sent yet.</p>}
        <ul className="rcm-threads">
          {sends.map((send) => (
            <li key={send.id} className="rcm-thread">
              <strong>{STATUS[send.status]}</strong>
              <small>
                {send.subject} · {send.recipients.join(', ')} ·{' '}
                {when.format(new Date(send.created_at))}
              </small>
              {send.error && <small>{send.error}</small>}
              {send.status === 'AWAITING_APPROVAL' && (
                <div className="sp-actions">
                  <button
                    type="button"
                    className="btn small"
                    onClick={() =>
                      void act(
                        () => emailApi.decide(organisationId, send.id, true),
                        'Approved — sending now.',
                      ).catch(() => undefined)
                    }
                  >
                    Approve &amp; send
                  </button>
                  <button
                    type="button"
                    className="btn small secondary"
                    onClick={() =>
                      void act(
                        () => emailApi.decide(organisationId, send.id, false),
                        'Rejected — nothing was sent.',
                      ).catch(() => undefined)
                    }
                  >
                    Reject
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
